import React, { useState } from 'react';
import {
    AppBar,
    Toolbar,
    IconButton,
    Typography,
    Container,
    Box,
    Rating,
    TextField,
    Button,
    ToggleButton,
    ToggleButtonGroup,
    Stack,
    createTheme,
    ThemeProvider,
    CssBaseline,
    Dialog,

} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { post } from '../../utility/fetch';

// Optional: Custom theme for consistent colors, etc.
const theme = createTheme({
    palette: {
        primary: {
            main: '#28a745', // A green similar to the submit button
        },
        secondary: {
            main: '#f8f9fa', // Light background or gray
        },
        text: {
            primary: '#333',
            secondary: '#6c757d',
        },
    },
    components: {
        MuiRating: {
            styleOverrides: {
                iconFilled: {
                    color: '#ffc107', // Gold for filled stars
                },
                iconEmpty: {
                    color: '#ced4da', // Gray for empty stars
                },
            },
        },
        MuiToggleButton: {
            styleOverrides: {
                root: {
                    textTransform: 'none', // Prevent uppercase
                    // Ensure a square aspect ratio or consistent size
                    minWidth: '40px', // Example fixed width
                    height: '40px', // Example fixed height
                    '&.Mui-selected': {
                        backgroundColor: '#28a745', // Selected green
                        color: '#fff',
                        '&:hover': {
                            backgroundColor: '#218838', // Darker green on hover
                        },
                    },
                },
            },
        },
        MuiButton: {
            styleOverrides: {
                root: {
                    textTransform: 'none', // Prevent uppercase
                }
            }
        }
    },
});

function DoctorRatingForm({handleRatingOpen,doctorId, appointmentId, ratingOpen}) {
    const [clarityRating, setClarityRating] = useState(0);
    const [satisfactionRating, setSatisfactionRating] = useState(0);
    const [recommendation, setRecommendation] = useState(null);
    const [feedback, setFeedback] = useState('');

    const handleSubmit = async (event) => {
        event.preventDefault();
        let payload = {
            appointmentId: appointmentId, // Example appointment ID
            doctorId: Number(doctorId), // Example doctor ID
            patientId: Number(sessionStorage?.getItem("userId")), // Example patient ID
            howAttentiveWasTheDoctorRate: clarityRating,
            howSatisfiedAreYouRate: satisfactionRating,
            recommendationRate: recommendation,
            moreDetails: feedback,
        }
        console.log({
            clarityRating,
            satisfactionRating,
            recommendation,
            feedback,
        });
        handleRatingOpen(); // Close the dialog after submission
        const res = await post('/doctorRating', payload);
        if (res?.status === 200) {
            console.log('Review submitted successfully:', res.data);
        } else {
            console.error('Error submitting review:', res);
        }
        alert('Review submitted!');
    };

    return (
        <Dialog open={ratingOpen} onClose={() => {handleRatingOpen()}}>
            <ThemeProvider theme={theme}>
                <CssBaseline />
                <Box sx={{ backgroundColor: '#f8f9fa', minHeight: '100vh' }}> {/* Light grey background */}
                    <AppBar position="static" color="transparent" elevation={0} sx={{ borderBottom: '1px solid #e0e0e0' }}>
                        <Toolbar>
                            <IconButton onClick={handleRatingOpen} edge="start" color="inherit" aria-label="back">
                                <ArrowBackIcon sx={{ color: '#6c757d' }} /> {/* Grey back arrow */}
                            </IconButton>
                            <Typography variant="h6" component="div" sx={{ flexGrow: 1, textAlign: 'center', color: '#333' }}>
                                Doctor's Rating
                            </Typography>
                            <Box sx={{ width: 48 }} /> {/* Placeholder for alignment */}
                        </Toolbar>
                    </AppBar>

                    <Container maxWidth="sm" sx={{ py: 3, px: { xs: 2, sm: 3 } }}>
                        <form onSubmit={handleSubmit}>
                            <Stack spacing={4}> {/* Consistent spacing between sections */}

                                {/* Clarity and Understanding */}
                                <Box>
                                    <Typography variant="body1" sx={{ mb: 1, color: '#333' }}>
                                        How well did the doctor listen to your concerns and explain things in a clear, understandable way?
                                    </Typography>
                                    <Rating
                                        name="clarity-rating"
                                        value={clarityRating}
                                        onChange={(event, newValue) => {
                                            setClarityRating(newValue);
                                        }}
                                        precision={1}
                                        size="large"
                                        sx={{ '& .MuiRating-iconEmpty': { color: '#ced4da' } }} // Ensure empty stars are grey
                                    />
                                </Box>

                                {/* Care and Treatment Satisfaction */}
                                <Box>
                                    <Typography variant="body1" sx={{ mb: 1, color: '#333' }}>
                                        How satisfied are you with the care and treatment provided during your visit?
                                    </Typography>
                                    <Rating
                                        name="satisfaction-rating"
                                        value={satisfactionRating}
                                        onChange={(event, newValue) => {
                                            setSatisfactionRating(newValue);
                                        }}
                                        precision={1}
                                        size="large"
                                        sx={{ '& .MuiRating-iconEmpty': { color: '#ced4da' } }}
                                    />
                                </Box>

                                {/* Recommendation Likelihood */}
                                <Box>
                                    <Typography variant="body1" sx={{ mb: 1, color: '#333' }}>
                                        How likely are you to recommend this doctor to family or friends?
                                    </Typography>
                                    <ToggleButtonGroup
                                        value={recommendation}
                                        exclusive
                                        onChange={(event, newRecommendation) => {
                                            setRecommendation(newRecommendation);
                                        }}
                                        aria-label="recommendation likelihood"
                                        sx={{
                                            display: 'flex', // Make buttons flex items
                                            flexWrap: 'wrap', // Allow buttons to wrap
                                            gap: 1, // Spacing between buttons
                                            justifyContent: { xs: 'center', sm: 'flex-start' } // Center on small screens
                                        }}
                                    >
                                        {[1, 2, 3, 4, 5].map((num) => (
                                            <ToggleButton
                                                key={num}
                                                value={num}
                                                aria-label={`${num} likelihood`}
                                                sx={{
                                                    backgroundColor: recommendation === num ? theme.palette.primary.main : '#e9ecef', // Light gray background for unselected
                                                    color: recommendation === num ? '#fff' : '#495057', // Dark text for unselected
                                                    borderColor: recommendation === num ? theme.palette.primary.main : '#ced4da', // Border color
                                                    borderWidth: '1px',
                                                    borderStyle: 'solid',
                                                    '&:hover': {
                                                        backgroundColor: recommendation === num ? theme.palette.primary.dark : '#dee2e6', // Darker on hover
                                                    },
                                                    borderRadius: '4px', // Slightly rounded corners
                                                    padding: '8px 12px', // Adjust padding
                                                    minWidth: 'unset', // Remove default min-width for better control
                                                    width: '40px', // Fixed width for number buttons
                                                    height: '40px', // Fixed height for number buttons
                                                }}
                                            >
                                                <Typography variant="body1" fontWeight="bold">{num}</Typography>
                                            </ToggleButton>
                                        ))}
                                    </ToggleButtonGroup>
                                    <Typography variant="caption" display="block" sx={{ mt: 1, color: '#6c757d' }}>
                                        (Rating: 1 - Not likely at all, 5 - Extremely likely)
                                    </Typography>
                                </Box>

                                {/* Feedback Text Area */}
                                <Box>
                                    <Typography variant="body1" sx={{ mb: 1, color: '#333' }}>
                                        Tell us more about his service
                                    </Typography>
                                    <TextField
                                        id="feedback"
                                        multiline
                                        rows={5}
                                        fullWidth
                                        variant="outlined"
                                        value={feedback}
                                        onChange={(e) => setFeedback(e.target.value)}
                                        placeholder="Type your feedback here..."
                                        sx={{
                                            backgroundColor: '#eafcec', // Light green background for text area
                                            '& .MuiOutlinedInput-notchedOutline': {
                                                borderColor: '#28a745', // Green border
                                            },
                                            '&:hover .MuiOutlinedInput-notchedOutline': {
                                                borderColor: '#218838', // Darker green on hover
                                            },
                                            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                                borderColor: '#218838', // Darker green when focused
                                                borderWidth: '2px', // Thicker border on focus
                                            },
                                        }}
                                    />
                                </Box>

                                {/* Submit Button */}
                                <Button
                                    type="submit"
                                    variant="contained"
                                    color="primary"
                                    fullWidth
                                    size="large"
                                    sx={{ py: 1.5, fontSize: '1rem', fontWeight: 'bold' }}
                                >
                                    Submit Review
                                </Button>

                            </Stack>
                        </form>
                    </Container>
                </Box>
            </ThemeProvider>
        </Dialog>
    );
}

export default DoctorRatingForm;